import type { Recommendation, SiteRagReport } from '../../types/siteRag';
import { projectReportSourceEvidence, SOURCE_ADMISSION_GAP_TEXT } from '../reportGeneration/reportEvidenceSourceAdmission';
import {
  deriveScreeningArchitectureRecommendations,
  deriveScreeningRecommendationSetV1,
  SCREENING_ARCHITECTURE_LINE_IDS,
  type ScreeningArchitectureRecommendation,
  type ScreeningArchitectureLineId,
  type ScreeningArchitectureRecommendations,
  type ScreeningRecommendationReport,
  type ScreeningRecommendationSetV1,
} from './screeningArchitectureRecommendations';
import { NUMBERED_REF_TOKEN_RE } from '../citationTokens';

/** Admit a displayed value and all its qualifications as one unit. */
export function presentScreeningRecommendationFields(
  report: ScreeningRecommendationReport,
  fields: readonly string[],
): string[] | null {
  const recommendations: Recommendation[] = fields.map(description => ({
    type: 'power', title: 'Saved recommendation field', description, rationale: '',
  }));
  const presented = projectReportSourceEvidence({ recommendations } as SiteRagReport, {
    references: report.references,
    sourceCoverage: report.screeningBasis?.sourceCoverage,
    ...(report.publicSentimentResearch?.section ? { publicSentiment: report.publicSentimentResearch.section } : {}),
  }).recommendations.map(row => row.description);
  return presented.some(text => text.includes(SOURCE_ADMISSION_GAP_TEXT)) ? null : presented;
}

/** Read-only source admission for displayed advice. Raw derivation remains the
 * generation/decision-snapshot contract; no saved report or receipt is changed.
 * Project the actual displayed fields, including detailed-BTM/pathway/campus
 * fallbacks, through the same source policy as full-report prose. If any part
 * of a recommendation has lost its supporting source, withhold that advice
 * rather than strip its marker and leave an unsupported positive claim. */
export function deriveScreeningRecommendationsForPresentation(
  report: ScreeningRecommendationReport | null | undefined,
  siteId?: string,
): ScreeningArchitectureRecommendations {
  const advice = deriveScreeningArchitectureRecommendations(report, siteId);
  const entries = Object.entries(advice).filter((entry): entry is [ScreeningArchitectureLineId, ScreeningArchitectureRecommendation] => entry[1] != null);
  if (!report || entries.length === 0) return {};
  const result: ScreeningArchitectureRecommendations = {};
  for (const [id, original] of entries) {
    // Some frozen model advice carries its sources separately from prose.
    const fields = presentScreeningRecommendationFields(report, [original.value, original.basisNote, original.citations.join(' ')]);
    if (!fields) continue;
    const [value, basisNote, sourceMarkers] = fields;
    const citations = [...new Set(`${value} ${basisNote} ${sourceMarkers}`
      .match(new RegExp(NUMBERED_REF_TOKEN_RE.source, NUMBERED_REF_TOKEN_RE.flags)) ?? [])];
    result[id] = { ...original, value: value!, basisNote: basisNote!, citations };
  }
  return result;
}

/**
 * Source-admitted `ScreeningRecommendationSetV1` for display surfaces (Full Report,
 * Summary, Brief, and Home). Admits both positive recommendations and explicit
 * saved unresolved reasons through `presentScreeningRecommendationFields`.
 */
export function derivePresentedScreeningRecommendationSetV1(
  report: ScreeningRecommendationReport | null | undefined,
  siteId?: string,
): ScreeningRecommendationSetV1 {
  const presented = deriveScreeningRecommendationsForPresentation(report, siteId);
  const set = deriveScreeningRecommendationSetV1(report, siteId, presented);
  if (!report) return set;
  for (const id of SCREENING_ARCHITECTURE_LINE_IDS) {
    const q = set.questions[id];
    if (q.status === 'alternatives' && q.alternatives?.length) {
      const admittedAlternatives: string[] = [];
      for (const alt of q.alternatives) {
        const admitted = presentScreeningRecommendationFields(report, [alt]);
        if (admitted?.[0]?.trim()) {
          admittedAlternatives.push(admitted[0].trim());
        }
      }
      if (admittedAlternatives.length === 0) {
        set.questions[id] = {
          ...q,
          status: 'withheld-source',
          alternatives: undefined,
          basisNote: 'Saved screening alternatives withheld because a cited source is unavailable.',
          unresolvedReason: 'Saved screening alternatives withheld because a cited source is unavailable.',
          citations: [],
        };
      } else {
        const citations = [...new Set(admittedAlternatives.join(' ')
          .match(new RegExp(NUMBERED_REF_TOKEN_RE.source, NUMBERED_REF_TOKEN_RE.flags)) ?? [])];
        set.questions[id] = {
          ...q,
          alternatives: admittedAlternatives,
          citations,
        };
      }
    } else if (q.status === 'unresolved' && q.unresolvedReason) {
      const admitted = presentScreeningRecommendationFields(report, [q.unresolvedReason, q.citations.join(' ')]);
      if (!admitted) {
        set.questions[id] = {
          ...q,
          status: 'withheld-source',
          basisNote: 'Saved screening explanation withheld because a cited source is unavailable.',
          unresolvedReason: 'Saved screening explanation withheld because a cited source is unavailable.',
          citations: [],
        };
      } else {
        const [reasonText, markers] = admitted;
        const citations = [...new Set(`${reasonText} ${markers}`
          .match(new RegExp(NUMBERED_REF_TOKEN_RE.source, NUMBERED_REF_TOKEN_RE.flags)) ?? [])];
        set.questions[id] = {
          ...q,
          basisNote: reasonText!,
          unresolvedReason: reasonText!,
          citations,
        };
      }
    }
  }
  return set;
}
